import React, { useEffect, useState } from "react";
import { Empty, Form, List, Space, Tooltip, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { WHITE, GRAY_900 } from "../../../../constant/colors";
import { fontWeights, sizes } from "../../../../constant/styles";
import { ApiNames } from "../../../../api/api";
import ACTIONS from "../../../../store/action";
import { selectEnrollmentNoteAddedSuccess, selectEnrollmentNotes, selectIsFetching } from "../../../../store/selector";
import { ApplicationNotesActionMenu } from "../PatientApplicationDetails/trackComponents/ApplicationNotesActionMenu";
import { ReactComponent as WarningIcon } from "../../../../assets/svg/warning-icon-popup.svg";
import { ExpandableNoteContent } from "../PatientApplicationDetails/trackComponents/ExpandableNoteContent";
import { getNoteHeaderLabel, getNoteTooltipContent } from "../../../../utils/editNotes";
import {
  TextAreaStyled,
  FormItemStyled,
  ValidationContainer,
  CharacterCount,
  EditContainer,
  EditNoteTextArea,
  NoteList,
  NoteItemContainer,
  ActionMenuWrapper,
  CreatedByLabel,
  NoteContent,
  ButtonContainerAddNote,
  AddNoteButton,
  CancelNoteButton,
  StyledEmptyText,
  HeaderContainer,
  NotesContainer,
  InputAreaContainer,
  ButtonsContainer,
  CancelEditNoteButton,
  NOTE_CHARACTER_LIMIT,
  EditNoteErrorContainer,
  FormItemStyledEditNote
} from "../../../sharedComponents/EditNotesUI";

const UNSAVED_ENROLLMENT_NOTE_KEY = "unsaved_enrollment_note";
export const PRESERVE_ENROLLMENT_NOTES_AFTER_FORM_SUBMIT_KEY = "preserve_unsaved_enrollment_notes_after_form_submit";

export const DATE_TIME_FORMAT = "MM/DD/YY – HH:mm";
export const APPLICATION_NOTE_TEXT_FIELD_NAME = "applicationNoteText";

export const PatientApplicationNotes = ({
  noteForm,
  setHasUnsavedNoteEdit,
  shouldResetHoveredNoteIdAndEditNoteInfo,
  setShouldResetHoveredNoteIdAndEditNoteInfo
}) => {
  const { t } = useTranslation();
  const { applicationId } = useParams();
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();

  const [hoveredNoteId, setHoveredNoteId] = useState(null);
  const [menuHoveredNoteId, setMenuHoveredNoteId] = useState(null);
  const [editingNoteInfo, setEditingNoteInfo] = useState({ id: null, note: "", hasUnsavedChanges: false });
  const [pendingNoteToEdit, setPendingNoteToEdit] = useState(null);
  const [showUnsavedNoteEditChangesModal, setShowUnsavedNoteEditChangesModal] = useState(false);

  const applicationNoteText = noteForm?.getFieldValue(APPLICATION_NOTE_TEXT_FIELD_NAME) || "";
  const isAddNoteButtonDisabled =
    !applicationNoteText.trim() ||
    applicationNoteText.trim().length > NOTE_CHARACTER_LIMIT ||
    editingNoteInfo.hasUnsavedChanges;
  const hasAddNoteError = !!noteForm?.getFieldError(APPLICATION_NOTE_TEXT_FIELD_NAME)?.[0];
  const disabledSaveEditNote =
    !editingNoteInfo.hasUnsavedChanges ||
    !editingNoteInfo.note.trim() ||
    editingNoteInfo.note.length > NOTE_CHARACTER_LIMIT;

  const enrollmentNotes = useSelector(selectEnrollmentNotes);
  const enrollmentNoteAddedSuccess = useSelector(selectEnrollmentNoteAddedSuccess);
  const isFetchingEnrollmentNotes = useSelector(selectIsFetching([ApiNames.GetAllEnrollmentNotes]));
  const isFetchingAddEnrollmentNote = useSelector(selectIsFetching([ApiNames.AddEnrollmentNote]));
  const isFetchingDeleteNote = useSelector(selectIsFetching([ApiNames.DeleteEnrollmentNote]));

  const getAllEnrollmentNotesAct = (applicationId) => dispatch(ACTIONS.getAllEnrollmentNotesAction(applicationId));
  const addEnrollmentNoteAct = (applicationId, note) => dispatch(ACTIONS.addEnrollmentNoteAction(applicationId, note));
  const resetNoteSuccessAct = () => dispatch(ACTIONS.resetEnrollmentNoteSuccess());
  const updateEnrollmentNoteAct = (noteId, note) => dispatch(ACTIONS.updateEnrollmentNoteAction(noteId, note));

  const clearHoveredNoteId = () => {
    setHoveredNoteId(null);
    setMenuHoveredNoteId(null);
  };

  useEffect(() => {
    if (shouldResetHoveredNoteIdAndEditNoteInfo) {
      clearHoveredNoteId();
      setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false });
      setShowUnsavedNoteEditChangesModal(false);
      setPendingNoteToEdit(null);
      setShouldResetHoveredNoteIdAndEditNoteInfo(false);
    }
  }, [shouldResetHoveredNoteIdAndEditNoteInfo]);

  useEffect(() => {
    const formSubmitted = localStorage.getItem(PRESERVE_ENROLLMENT_NOTES_AFTER_FORM_SUBMIT_KEY);
    if (formSubmitted === "true") {
      const savedNote = localStorage.getItem(UNSAVED_ENROLLMENT_NOTE_KEY);
      if (savedNote) {
        noteForm.setFieldValue(APPLICATION_NOTE_TEXT_FIELD_NAME, savedNote);
      }
      localStorage.removeItem(PRESERVE_ENROLLMENT_NOTES_AFTER_FORM_SUBMIT_KEY);
    }
  }, []);

  useEffect(() => {
    if (applicationNoteText.trim()) {
      localStorage.setItem(UNSAVED_ENROLLMENT_NOTE_KEY, applicationNoteText);
    } else {
      localStorage.removeItem(UNSAVED_ENROLLMENT_NOTE_KEY);
      localStorage.removeItem(PRESERVE_ENROLLMENT_NOTES_AFTER_FORM_SUBMIT_KEY);
    }
  }, [applicationNoteText]);

  const handleMenuHoverChange = (noteId, isHovered) => {
    setMenuHoveredNoteId(isHovered ? noteId : null);
  };

  const handleEditNote = (note) => {
    if (editingNoteInfo.id && editingNoteInfo.hasUnsavedChanges && editingNoteInfo.id !== note.id) {
      setPendingNoteToEdit(note);
      setShowUnsavedNoteEditChangesModal(true);
      return;
    }

    setEditingNoteInfo({ id: note.id, note: note.note, hasUnsavedChanges: false });
    noteForm.setFieldValue(`editedNoteText-${note.id}`, note.note);
  };

  const handleEditNoteTextChange = (e) => {
    const newText = e.target.value;
    const originalContent = getOriginalNoteContent(editingNoteInfo.id);
    const hasChanges = newText.trim() !== originalContent.trim();

    setEditingNoteInfo({
      ...editingNoteInfo,
      note: newText,
      hasUnsavedChanges: hasChanges
    });

    // Trigger validation for the edit note field
    const fieldName = `editedNoteText-${editingNoteInfo.id}`;
    if (newText.length > NOTE_CHARACTER_LIMIT) {
      noteForm.setFields([
        {
          name: fieldName,
          value: newText,
          errors: [t("patientApplications.claims_tab.error_messages.notes_length_error")]
        }
      ]);
    } else {
      noteForm.setFields([
        {
          name: fieldName,
          value: newText,
          errors: []
        }
      ]);
    }
  };

  const handleContinueAfterEditNoteAction = () => {
    if (pendingNoteToEdit) {
      setEditingNoteInfo({ id: pendingNoteToEdit.id, note: pendingNoteToEdit.note, hasUnsavedChanges: false });
      noteForm.setFieldValue(`editedNoteText-${pendingNoteToEdit.id}`, pendingNoteToEdit.note);
    } else {
      setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false });
    }

    setShowUnsavedNoteEditChangesModal(false);
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleDiscardEditNoteAndContinue = () => {
    handleContinueAfterEditNoteAction();
  };

  const getOriginalNoteContent = (noteId) => {
    const originalNote = enrollmentNotes.find((note) => note.id === noteId);
    return originalNote ? originalNote.note : "";
  };

  const handleSaveEdit = () => {
    const fieldName = `editedNoteText-${editingNoteInfo.id}`;
    noteForm
      .validateFields([fieldName])
      .then((values) => {
        if (values[fieldName]?.trim() && values[fieldName] !== getOriginalNoteContent(editingNoteInfo.id)) {
          updateEnrollmentNoteAct(editingNoteInfo.id, {
            note: values[fieldName].trim()
          });
          noteForm.resetFields([fieldName]);
          handleCancelEdit();
          setMenuHoveredNoteId(null);
        } else {
          handleCancelEdit();
        }
      })
      .catch(() => {});
  };

  const handleCancelEdit = () => {
    if (editingNoteInfo.id) {
      noteForm.resetFields([`editedNoteText-${editingNoteInfo.id}`]);
    }
    setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false });
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleReturnToEditMode = () => {
    setShowUnsavedNoteEditChangesModal(false);
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleAddNote = () => {
    noteForm
      .validateFields([APPLICATION_NOTE_TEXT_FIELD_NAME])
      .then((values) => {
        if (values.applicationNoteText?.trim()) {
          addEnrollmentNoteAct(applicationId, values.applicationNoteText.trim());
        }
      })
      .catch(() => {});
  };

  const handleNoteChange = (e) => {
    const value = e.target.value;

    if (value.length > NOTE_CHARACTER_LIMIT) {
      noteForm.setFields([
        {
          name: APPLICATION_NOTE_TEXT_FIELD_NAME,
          value: value,
          errors: [t("patientApplications.track_tab.error_messages.notes_length_error")]
        }
      ]);
    } else {
      noteForm.setFields([
        {
          name: APPLICATION_NOTE_TEXT_FIELD_NAME,
          value: value,
          errors: []
        }
      ]);
    }
  };

  useEffect(() => {
    if (applicationId) {
      getAllEnrollmentNotesAct(applicationId);
    }
  }, [applicationId]);

  useEffect(() => {
    if (enrollmentNoteAddedSuccess) {
      noteForm.resetFields();
      resetNoteSuccessAct();
    }
  }, [enrollmentNoteAddedSuccess]);

  useEffect(() => {
    if (showUnsavedNoteEditChangesModal) {
      modal.confirm({
        centered: true,
        okText: t("yes_sure"),
        cancelText: t("back"),
        icon: <WarningIcon />,
        title: t("pop_up_messages.unsaved_changes"),
        content: <>{t("pop_up_messages.you_have_unsaved_changes_in_your_note")}</>,
        onOk: handleDiscardEditNoteAndContinue,
        onCancel: handleReturnToEditMode,
        okButtonProps: {
          id: "yes_sure_button"
        },
        cancelButtonProps: {
          id: "back_button"
        }
      });
    }
  }, [showUnsavedNoteEditChangesModal, pendingNoteToEdit]);

  useEffect(() => {
    setHasUnsavedNoteEdit(editingNoteInfo.hasUnsavedChanges);
  }, [editingNoteInfo.hasUnsavedChanges]);

  return (
    <NotesContainer>
      <HeaderContainer>{t("patientApplications.track_tab.notes")}</HeaderContainer>
      <InputAreaContainer>
        <Form form={noteForm} layout="vertical">
          <FormItemStyled
            name={APPLICATION_NOTE_TEXT_FIELD_NAME}
            validateTrigger={["onChange"]}
            rules={[
              {
                max: NOTE_CHARACTER_LIMIT,
                message: t("patientApplications.track_tab.error_messages.notes_length_error")
              }
            ]}
          >
            <TextAreaStyled
              placeholder={t("patientApplications.track_tab.add_a_new_note")}
              showCount
              disabled={editingNoteInfo.hasUnsavedChanges}
              onChange={handleNoteChange}
            />
          </FormItemStyled>
          <ValidationContainer hasError={hasAddNoteError}>
            <CharacterCount
              hasError={
                hasAddNoteError &&
                noteForm?.getFieldValue(APPLICATION_NOTE_TEXT_FIELD_NAME)?.length > NOTE_CHARACTER_LIMIT
              }
            >
              {noteForm?.getFieldValue(APPLICATION_NOTE_TEXT_FIELD_NAME)?.length || 0}/{NOTE_CHARACTER_LIMIT}
            </CharacterCount>
            {noteForm?.getFieldError(APPLICATION_NOTE_TEXT_FIELD_NAME)?.[0] && (
              <div>{noteForm.getFieldError(APPLICATION_NOTE_TEXT_FIELD_NAME)[0]}</div>
            )}
          </ValidationContainer>

          <ButtonContainerAddNote>
            <CancelNoteButton
              type="link"
              onClick={() => {
                noteForm.resetFields();
              }}
              disabled={!applicationNoteText.trim()}
              id="cancel_note_button"
            >
              {t("patientApplications.track_tab.cancel")}
            </CancelNoteButton>

            <AddNoteButton
              type="primary"
              onClick={handleAddNote}
              loading={isFetchingAddEnrollmentNote}
              disabled={isAddNoteButtonDisabled}
              id="save_note_button"
            >
              {!isFetchingAddEnrollmentNote && t("patientApplications.track_tab.save_note")}
            </AddNoteButton>
          </ButtonContainerAddNote>
        </Form>
      </InputAreaContainer>
      <NoteList
        loading={isFetchingEnrollmentNotes || isFetchingDeleteNote || isFetchingAddEnrollmentNote}
        dataSource={enrollmentNotes}
        locale={{
          emptyText: (
            <Empty
              description={
                <Space direction="vertical" size={0}>
                  <StyledEmptyText>{t("patientApplications.track_tab.no_notes_yet")}</StyledEmptyText>
                  <StyledEmptyText>
                    {t("patientApplications.track_tab.start_by_typing_in_the_box_above")}
                  </StyledEmptyText>
                </Space>
              }
            />
          )
        }}
        renderItem={(note, index) => {
          const { createdByUserName, updatedByUserName, createdAt, updatedAt } = note || {};
          const isEditing = editingNoteInfo?.id === note.id;

          const noteHeaderLabel = getNoteHeaderLabel({ t, updatedByUserName, createdByUserName, updatedAt, createdAt });
          const noteTooltipContent = getNoteTooltipContent({
            t,
            updatedByUserName,
            createdByUserName,
            createdAt,
            updatedAt
          });
          const hasEditNoteError = !!noteForm.getFieldError(`editedNoteText-${note.id}`)?.[0];

          return (
            <List.Item
              key={index}
              onMouseEnter={() => setHoveredNoteId(note.id)}
              onMouseLeave={() => {
                if (menuHoveredNoteId !== note.id) {
                  setHoveredNoteId(null);
                }
              }}
            >
              <List.Item.Meta
                key={index}
                id={note.id}
                description={
                  <NoteItemContainer isEditing={isEditing}>
                    <Space direction="vertical" size={isEditing ? 5 : 0} style={{ width: "100%" }}>
                      <Tooltip
                        title={noteTooltipContent}
                        id="note_header_tooltip"
                        placement="top"
                        overlayInnerStyle={{
                          backgroundColor: GRAY_900,
                          borderRadius: "8px",
                          fontWeight: fontWeights.semibold,
                          fontSize: sizes.small,
                          color: WHITE
                        }}
                        color={GRAY_900}
                        overlayStyle={{
                          maxWidth: "400px",
                          whiteSpace: "pre-line"
                        }}
                      >
                        <CreatedByLabel id="note_header_label">{noteHeaderLabel}</CreatedByLabel>
                      </Tooltip>
                      {isEditing ? (
                        <Form form={noteForm} layout="vertical">
                          <EditContainer>
                            <FormItemStyledEditNote
                              name={`editedNoteText-${note.id}`}
                              initialValue={note.note}
                              rules={[
                                {
                                  max: NOTE_CHARACTER_LIMIT,
                                  message: t("patientApplications.track_tab.error_messages.notes_length_error")
                                }
                              ]}
                            >
                              <EditNoteTextArea
                                onChange={handleEditNoteTextChange}
                                showCount
                                autoSize={false}
                                placeholder={t("patientApplications.track_tab.edit_note_placeholder")}
                                id="edit_note_text_area"
                                hasError={hasEditNoteError}
                              />
                            </FormItemStyledEditNote>

                            <ButtonsContainer>
                              <EditNoteErrorContainer hasError={hasEditNoteError}>
                                <CharacterCount hasError={hasEditNoteError}>
                                  {editingNoteInfo.note?.length || 0}/{NOTE_CHARACTER_LIMIT}
                                </CharacterCount>
                                <div>{noteForm.getFieldError(`editedNoteText-${note.id}`)?.[0]}</div>
                              </EditNoteErrorContainer>

                              <div style={{ gap: "8px", display: "flex", flexDirection: "column" }}>
                                <AddNoteButton
                                  type="primary"
                                  onClick={handleSaveEdit}
                                  disabled={disabledSaveEditNote}
                                  isEditing={true}
                                  id="save_note_button"
                                >
                                  {t("patientApplications.track_tab.save_note")}
                                </AddNoteButton>
                                <CancelEditNoteButton type="link" onClick={handleCancelEdit} id="cancel_note_button">
                                  {t("patientApplications.track_tab.cancel")}
                                </CancelEditNoteButton>
                              </div>
                            </ButtonsContainer>
                          </EditContainer>
                        </Form>
                      ) : (
                        <NoteContent id="note_content">
                          <ExpandableNoteContent>{note.note}</ExpandableNoteContent>
                        </NoteContent>
                      )}
                    </Space>
                    {!isEditing && (
                      <ActionMenuWrapper
                        className="note-action-menu"
                        isVisible={hoveredNoteId === note.id || menuHoveredNoteId === note.id}
                      >
                        <ApplicationNotesActionMenu
                          note={note}
                          isVisible={hoveredNoteId === note.id || menuHoveredNoteId === note.id}
                          onMenuHoverChange={(isHovered) => handleMenuHoverChange(note.id, isHovered)}
                          onEditNote={handleEditNote}
                          setHoveredNoteId={setHoveredNoteId}
                        />
                      </ActionMenuWrapper>
                    )}
                  </NoteItemContainer>
                }
              />
            </List.Item>
          );
        }}
      />
      {contextHolder}
    </NotesContainer>
  );
};
